import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, TestTube } from "lucide-react";

import RequestForm from "../components/concierge-testing/RequestForm";
import PatientRequestsView from "../components/concierge-testing/PatientRequestsView";
import DoctorRequestsView from "../components/concierge-testing/DoctorRequestsView";
import RequestDetail from "../components/concierge-testing/RequestDetail";

import {
  useConciergeTestRequests,
  useCreateConciergeTestRequest,
  useUpdateConciergeTestRequest,
  useDeleteConciergeTestRequest,
  useUpdateConciergeTestRequestStatus,
} from "@/hooks/useConciergeTests";
import { useAuth } from "@/hooks/useAuth";

export default function ConciergeTesting() {
  const [showForm, setShowForm] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [viewingRequest, setViewingRequest] = useState(null);
  const { hasPermission, user } = useAuth();

  // Query for fetching requests with permission check
  const {
    data: requests = [],
    isLoading,
    error,
  } = useConciergeTestRequests(
    hasPermission("view_concierge_testing") ? {
      orderBy: "createdAt",
      orderDirection: "desc",
    } : {}
  );

  // Mutations
  const createMutation = useCreateConciergeTestRequest();
  const updateMutation = useUpdateConciergeTestRequest();
  const deleteMutation = useDeleteConciergeTestRequest();
  const statusUpdateMutation = useUpdateConciergeTestRequestStatus();

  const handleSaveRequest = async (requestData) => {
    try {
      if (editingRequest) {
        // Update existing request
        await updateMutation.mutateAsync({
          requestId: editingRequest.id,
          requestData,
        });
      } else {
        // Create new request
        await createMutation.mutateAsync(requestData);

        // TODO: Send email notification for new requests only
        // This would require implementing email service
        // const emailTemplate = generateTestaroRequestEmail(savedRequest);
        // await emailService.sendEmail({
        //   to: "lab@testaro.com",
        //   from_name: "ClubMed Concierge System",
        //   subject: emailTemplate.subject,
        //   body: emailTemplate.html,
        // });
      }
      // Close form on success
      setShowForm(false);
      setEditingRequest(null);
    } catch (error) {
      console.error("Failed to save concierge request:", error);
      // You might want to show a toast notification here
    }
  };

  const handleEditRequest = (request) => {
    setEditingRequest(request);
    setViewingRequest(null); // Close detail view if open
    setShowForm(true);
  };

  const handleViewRequest = (request) => {
    setViewingRequest(request);
    setShowForm(false); // Close form if open
  };

  const handleDeleteRequest = async (requestId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this concierge testing request? This action cannot be undone."
      )
    ) {
      try {
        await deleteMutation.mutateAsync(requestId);
      } catch (error) {
        console.error("Failed to delete concierge request:", error);
        // You might want to show a toast notification here
      }
    }
  };

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      const request = requests.find((r) => r.id === requestId);
      if (!request) return;

      const additionalData = {};

      // Add additional fields based on status
      if (newStatus === "scheduled") {
        additionalData.assigned_nurse =
          request.assigned_nurse || "Nurse Patricia";
        additionalData.scheduled_visit_datetime =
          request.scheduled_visit_datetime ||
          new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(); // Tomorrow
      }

      await statusUpdateMutation.mutateAsync({
        requestId,
        status: newStatus,
        additionalData,
      });
    } catch (error) {
      console.error("Failed to update request status:", error);
      // You might want to show a toast notification here
    }
  };

  const handleNewRequest = () => {
    setEditingRequest(null);
    setViewingRequest(null); // Close detail view if open
    setShowForm(true);
  };

  // Patients can view but cannot create/edit/delete
  const isPatient = user && user.userType === "patient";
  const canManageRequests = !isPatient;

  // Show loading state
  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-slate-600">
              Loading concierge testing requests...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-600 mb-4">
              Error loading concierge testing requests
            </p>
            <p className="text-slate-600 text-sm">{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2 flex items-center gap-3">
            <TestTube className="w-8 h-8 text-blue-600" />
            {isPatient ? "My Test Requests" : "Concierge Testing Requests"}
          </h1>
          <p className="text-slate-600">
            {isPatient 
              ? "View your at-home blood testing requests and results from Testaro Labs."
              : "Request at-home blood testing for patients from Testaro Labs."
            }
          </p>
        </div>
        {canManageRequests && (
          <div className="flex gap-3">
            <Button
              onClick={handleNewRequest}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Request
            </Button>
          </div>
        )}
      </div>

      {/* Request Form Modal */}
      {showForm && canManageRequests && (
        <RequestForm
          request={editingRequest}
          onSave={handleSaveRequest}
          onClose={() => {
            setShowForm(false);
            setEditingRequest(null);
          }}
        />
      )}

      {/* Request Detail View */}
      {viewingRequest ? (
        <RequestDetail
          request={viewingRequest}
          onClose={() => setViewingRequest(null)}
          onEdit={canManageRequests ? handleEditRequest : undefined}
          onDelete={canManageRequests ? handleDeleteRequest : undefined}
          onStatusChange={canManageRequests ? handleStatusChange : undefined}
          isPatientView={isPatient}
        />
      ) : (
        /* List of Requests */
        isPatient ? (
          <PatientRequestsView 
            requests={requests}
            isLoading={isLoading}
            onView={handleViewRequest}
          />
        ) : (
          <DoctorRequestsView 
            requests={requests}
            isLoading={isLoading}
            onStatusChange={handleStatusChange}
            onEdit={handleEditRequest}
            onDelete={handleDeleteRequest}
            onView={handleViewRequest}
          />
        )
      )}
    </div>
  );
}
